export enum OrderDetailsSections {
    OrderSummary = 'Order summary',
    ProductsOrdered = 'Products ordered',
    HelpAndSupport = 'Help and support',
}

export enum OrderDetailsPageHeaders {
    OrderDetails = 'Order details',
}

export enum OrderDetailsBreadcrumbLabels {
    Home = 'Home',
    Tracking = 'Tracking',
}

export enum OrderSummarySurfaceCornerRadius {
    Soft = 'cornerRadiusSoft',
    None = 'cornerRadiusNone',
}

export enum ProductsTableConfig {
    PageSize = 10,
}

export enum ProductSheetTabRoutes {
    ProductDetails = 'details',
    ProgressTracker = 'progresstracker'
}

export const ProductSheetTabIndexes = Object.keys(ProductSheetTabRoutes).reduce(
    (acc, key) => {
        return {
            ...acc,
            [ProductSheetTabRoutes[key]]: Object.keys(ProductSheetTabRoutes).indexOf(
                key
            ),
        };
    },
    {}
);

export enum ProductSheetTabLabels {
    ProductDetails = 'Product details',
    ProgressTracker = 'Progress tracker',
}

export enum ProductSheetProductDetails {
    Title = 'Product details',
    ProductProgress = 'Product progress',
    NoActionRequired = 'No action required',
    EstimatedCompletion = 'Estimated completion',
    TelstraOrderContact = 'Telstra order contact',
    WaitingForInformation = 'Waiting for information',
    ActionButtonLabel = 'Order enquiry (opens new tab)',
    OrderEnquiry = 'https://www.telstra.com.au/forms/fulfilment-delivery-control-order-enquiry',
    ActivityType = 'Activity type',
    BillingAccount = 'Billing account',
    OrderNumber = 'Order number',
    helperText = 'We\'re working on resolving the delay. Get in touch with your Telstra order contact for updates:'
}

export enum ProductSheetProgressTracker {
    Title = 'Progress tracker',
}

export enum ProductSheetLocationDetails {
    Title = 'Location and contact details',
    SiteName = 'Site name',
    SiteAddress = 'Site address',
    SiteContact = 'Site contact',
    DeliveryAddressDetails = 'Delivery address and details',
    CustomerProjectContact = 'Customer project contact',
    DeliveryContact = 'Delivery contact'
}

export enum ProductSheetAppointmentDetails {
    Title = 'Appointment information',
    AppointmentStatus = 'Appointment status',
    AppointmentDate = 'Appointment date'
}

export enum ProductSheetEquipmentDetails {
    Title = 'Equipment details',
    ConsignmentNumber = 'Consignment number',
    EstimatedDeliveryDate = 'Estimated delivery date',
    DeliveryStatus = 'Delivery status',
    ActionButtonLabel = 'Track your consignment (opens new tab)'
}

export enum OrderDetailsErrorMessagelabels {
    Title = 'Data not available right now',
    HelperText = 'Refresh the page or submit a support enquiry if there\'s still a problem.',
    primaryButtonLabel = 'Refresh page',
    secondaryButtonLabel = 'Submit enquiry'
}

export enum OrderTimingSectionLabels {
    Title = 'Order timing',
    Status = 'Order status',
    HelperText = 'Estimated date when all stages of the order are completed.',
    OrderCompletedLable = 'Order completed',
    EstimatedCompletionLable = 'Estimated order completion',
}

export enum HelpAndSupport {
    helpAndSupport = 'Help and support',
    submitEnquiry = 'Submit an enquiry',
    faq = 'Frequently asked questions',
    submitEnquiryRoute = '/app/customer-support/new-enquiry',
    faqRoute = '/app/support/faq',
    SupportContact = '+61 13 22 53'
}

export enum ProductDefaultValues {
    SiteAddress = 'Mobile or virtual service',
    NotApplicable = 'N/A'
}

export enum ProductProgress {
    Completed = 'completed',
    OnTrack = 'on track',
    Delayed = 'delayed'
}

export const VariantProgressMapping = {
    [ProductProgress.Completed]: 'NeutralEmphasis',
    [ProductProgress.OnTrack]: 'Positive',
    [ProductProgress.Delayed]: 'Negative'
} as const;

export enum JeopardyCategories {
    ActionRequired = 'Action required',
    NoActionRequired = 'No action required',
}

export const ProductJeopardyCategoryMessage = {
    [JeopardyCategories.ActionRequired]: 'Check your email for actions we need from you. Get in touch with your Telstra order contact if you\'re not sure.',
    [JeopardyCategories.NoActionRequired]: 'We\'re working on resolving the delay. Get in touch with your Telstra order contact for updates.'
} as const;

export type ProductJeopardyCategoryMessageType = typeof ProductJeopardyCategoryMessage[JeopardyCategories];

export enum ProductStatus {
    Closed = 'closed',
    Cancelled = 'cancelled'
}

export const CeaseAndCancel = {
    OrderSubmitted: {
        NotStarted: 'We\'ve got your request to cancel this service.',
        InProgress: 'We\'ve got your request to cancel this service.',
        Completed: 'Your request to cancel this service has progressed to the next stage.'
    },
    GettingThingsReady: {
        NotStarted: 'We\'re preparing to cancel your service.',
        InProgress: 'We\'re preparing to cancel your service.',
        Completed: 'We\'ve finished the necessary activities to cancel your service.'
    },
    Completed: {
        NotStarted: 'We\'re cancelling your service and billing for this service will stop.',
        InProgress: 'We\'re cancelling your service and billing for this service will stop.',
        Completed: 'We\'ve cancelled your service and billing for this service has stopped.'
    }
}

export const DefaultContent = {
    OrderSubmitted: {
        NotStarted: 'We’ve got your order.  We’ll let you know if we need anything from you to progress. ',
        InProgress: 'We’ve got your order.  We’ll let you know if we need anything from you to progress. ',
        Completed: 'Your order has progressed to the next stage where we\'ll be getting things ready.'
    },
    GettingThingsReady: {
        NotStarted: 'While preparing for your new service, we might need to:',
        InProgress: 'While preparing for your new service, we might need to:',
        Completed: 'We\'ve finished getting everything ready to activate your new service. '
    },
    ActivatingService: {
        NotStarted: 'We\'ll activate and test your new service. When the order is completed, we\'ll email your new service details.',
        InProgress: 'We\'ll activate and test your new service. When the order is completed, we\'ll email your new service details.',
        Completed: 'We\'ve activated your service and finished our testing ready for completion of your order.\n' +
            'For network orders, we\'ve finished preparing your site and have installed any new devices.'
    },
    Completed: {
        NotStarted: 'Your service is ready for use, and we\'ll start billing. We\'ll email the billing details for your new service.',
        InProgress: 'Your service is ready for use, and we\'ll start billing. We\'ll email the billing details for your new service.',
        Completed: 'Your service is ready to use and billing has commenced.'
    },

}

export const ActivityType = {
    Cease: 'cease',
    Cancel: 'cancel',
}

export const ActivityState = {
    NotStarted: 'Not started',
    InProgress: 'In progress',
    Completed: 'Completed'
}

export const ActivityStateLookup = {
    'Not started': 'NotStarted',
    'In progress': 'InProgress',
    'Completed': 'Completed'
}

export const ProgressTrackerStatus = {
    NotStarted: 'notStarted',
    InProgress: 'InProgress',
    Completed: 'Success',
    Warning: 'Warning',
    Error: 'Error'
}

export const ProgressTrackerStages = {
    OrderSubmitted: 'Order submitted',
    GettingThingsReady: 'Getting things ready',
    ActivatingService: 'Activating service',
    Completed: 'Completed',
}

export const ProgressTrackerStagesLookUp = {
    'Order submitted': 'OrderSubmitted',
    'Getting things ready': 'GettingThingsReady',
    'Activating service': 'ActivatingService',
    'Completed': 'Completed',
}

export enum OrderListHeaderSection {
    Header = 'Orders',
    Label = 'New orders may take a few hours to show. Showing all active orders and closed orders from the last 45 days.'
}

export enum OrderListErrorMessageLabels {
    Title = 'Data not available right now',
    HelperText = 'Refresh the page or submit a support enquiry if there\'s still a problem.',
    primaryButtonLabel = 'Refresh page',
    secondaryButtonLabel = 'Submit enquiry'
}

export enum OrderDownloadLabels {
    ErrorTitle = 'Exporting table error',
    ErrorDescription = 'There\'s an issue with your table export. You can select to try again or cancel.',
    InProgressTitle = 'Exporting table',
    CancelButton = 'Cancel',
    TryAgainButton = 'Try again',
    SuccessTitle = 'File has been exported',
    SuccessDescription = 'You can go back to export more files.',
    SuccessAction = 'OK'
}

export enum ProductOrderFilterActionLabels {
    AllActions = 'All actions',
    AllFilters = 'All filters',
    AppliedFilters = 'Applied Filters',
    SelectColumns = 'Select columns',
    ExportTable = 'Export table',
    SelectExport = 'Select export table',
    ExportButtonCsv = 'CSV',
    ExportButtonPdf = 'PDF',
    SearchPlaceholder = 'Search table',
    SearchHelpText = 'Minimum 3 characters to search',
    SearchError =  'Invalid entry. Enter at least 3 characters.'
}

export enum OrderListTableConfig {
    DefaultCurrentPage = 1
}

export const ShopHomePage = {
    Url: '/app/shop'
}

export enum ErrorTemplateButtonLabels {
    StartNewOrder = 'Start new order',
    Refresh = 'Refresh'
}

export enum StaffAccessModalConsts {
    Title = 'Staff view is read only',
    Description = 'Some areas of staff view have limited access or functionality.'
}
